import React, { useState, useEffect, useRef } from 'react';
import {
  Switch,
  Route,
  useHistory,
  useParams,
} from "react-router-dom";
import { useWindowSize } from '@react-hook/window-size'
import classnames from 'classnames'

import RegionBlock from './RegionBlock/'
import Header from './Header';
import About from './About';
import Resources from './Resources';
import Footer from './Footer';
import Timelapse from './Timelapse'

import { nycCounties, nycFips } from './geographicExceptions';
import { getText } from './languages'
import { statesByName } from './data/usStates'

import './Viz.scss'

export default function Viz(props) {
  const {
    type,
    populations,
    regionsById,
    dateUpdated,
    embedParams,
    directionality,
    language,
    setLanguage,
    conditionallyLoadRegionData,
  } = props;
  const isWorld = type === 'world';
  const routePath = isWorld ? 'country' : 'county';

  const [regionTextValue, setRegionTextValue] = useState('')
  const [transitioningOutId, setTransitioningOutId] = useState(null)
  const [updateMode, setUpdateMode] = useState(null)
  const [transitionTopLevelMapDown, setTransitionTopLevelMapDown] = useState(false)
  const [showUpdateMessage, setShowUpdateMessage] = useState(!document.location.search.includes('dateOverride'));
  const [forceMetricKey, setForceMetricKey] = useState({})

  const history = useHistory();
  const params = useParams();
  const windowSize = useWindowSize();

  let selectedRegionId = null;
  let selectedRegion = null;
  let selectedStateAbbr = null;
  let selectedParentId = null;
  let selectedParentDatum = null;
  let transitioningOutRegion = false;
  let transitioningOutState = false;

  const replaceUrlParamTimeout = useRef(null)
  const transitionMapDownTimeout = useRef(null)

  function goToWorldFromUS() {
    window.location = process.env.REACT_APP_WORLDLOCATION;
  }

  function goToUSFromWorld(id) {
    const isUS = id === '840';
    if (isUS) {
      selectedRegionId = null;
      clearTimeout(replaceUrlParamTimeout.current);
      clearTimeout(transitionMapDownTimeout.current);
      window.location = process.env.REACT_APP_USLOCATION;
    }
    return isUS;
  }

  let urlParamsId = null;
  let isGoingToUS = null;
  if ('id' in params) {
    urlParamsId = decodeURIComponent(params['id'])
    let regionId = urlParamsId

    if (transitioningOutId) {
      transitioningOutRegion = true
      regionId = transitioningOutId.previous
      if (transitioningOutId.next.substr(0, 2) !== regionId.substr(0, 2)) {
        transitioningOutState = true
      }
    }
    selectedRegionId = regionId
    if (regionId in regionsById) {
      const findMatchingDatum = regionsById[regionId]
      if (findMatchingDatum) {
        selectedRegion = findMatchingDatum
      }
    }
    const population = populations[regionId]

    //
    // switch for non-US state selection here
    if (regionsById[selectedRegionId].parent) {
      selectedParentId = regionsById[selectedRegionId].parent;
      if (regionsById[selectedParentId]) {
        selectedParentDatum = regionsById[selectedParentId];
      }
    }
    //

    if (population && !isWorld) {
      selectedStateAbbr = statesByName[population.STNAME].abbreviation
    }

    isGoingToUS = goToUSFromWorld(regionId);
  }

  const updateSelectedRegionInUrl = (id, label, mode) => {
    const isUS = goToUSFromWorld(id); // selecting US from World
    if (!isUS) {
      setUpdateMode(mode)
      const clickFromTopLevelMap = mode.mode === 'map' && (mode.map === 'usa' || mode.map === 'world')
      if (selectedRegionId && id !== selectedRegionId && !clickFromTopLevelMap) {
        clearTimeout(replaceUrlParamTimeout.current)
        setTransitioningOutId({ previous: selectedRegionId, next: id, label })
      } else {
        if (!selectedRegionId && clickFromTopLevelMap) {
          clearTimeout(transitionMapDownTimeout.current)
          setTransitionTopLevelMapDown({ next: id, label, mode })
        } else {
          history.replace(`/${routePath}/${encodeURIComponent(id)}`)
          setRegionTextValue(label)
        }
      }
    }
  }

  useEffect(() => {
    if (transitioningOutId !== null) {
      replaceUrlParamTimeout.current = setTimeout(() => {
          history.replace(`/${routePath}/${encodeURIComponent(transitioningOutId.next)}`)
          setRegionTextValue(transitioningOutId.label)
          setTransitioningOutId(null)
        }, 300)
    }
  }, [transitioningOutId, history, replaceUrlParamTimeout, routePath])

  useEffect(() => {
    if (updateMode) {
      if (
        updateMode.mode === 'map'
          &&
        (
          (updateMode.map === 'usa' || updateMode.map === 'world' || updateMode.map === 'state')
            ||
          (updateMode.map === 'country' && updateMode.subnational)
        )
      ) {
        window.scrollTo({ top: 0, left: 0 })
      }
      setUpdateMode(null)
    }
  }, [updateMode])

  useEffect(() => {
    if (transitionTopLevelMapDown) {
      history.push(`/${routePath}/${encodeURIComponent(transitionTopLevelMapDown.next)}`)
      setRegionTextValue(transitionTopLevelMapDown.label)
      transitionMapDownTimeout.current = setTimeout(() => {
        setTransitionTopLevelMapDown(false)
      }, 1000)
    }
  }, [transitionTopLevelMapDown, history, routePath])

  useEffect(() => {
    if (selectedRegionId) {
      if (conditionallyLoadRegionData) {
        conditionallyLoadRegionData(selectedRegionId)
      }
    }
  }, [selectedRegionId, conditionallyLoadRegionData])
  const setRegionValue = (regionValue) => {
    let { id, label } = regionValue;
    setRegionTextValue(isWorld ? getText(language, label) : label)
    const isNYCCounty = !!nycCounties.find(d => d.fips === id)
    if (isNYCCounty) {
      id = nycFips
    }
    if (populations[id]) {
      const regionLabel = isWorld
        ? getText(language, populations[id].name)
        : `${populations[id].CTYNAME}, ${populations[id].stateAbbr}`;
      updateSelectedRegionInUrl(id, regionLabel, { mode: 'autosuggest' })
    }
  }

  const clearForceMetricKey = () => {
    if (Object.keys(forceMetricKey).length !== 0) {
      setForceMetricKey({});
    }
  };

  const clickRegion = (fromWhichMap) => (clickedRegion, metricKey) => {
    return (event) => {
      if (event.defaultPrevented && !event.touches) { // prevents selection when dragging
        return;
      }

      if (embedParams) {
        const usParams = ['usa', 'county', 'state', 'stateCounty'];
        const worldParams = ['world', 'country'];
        const url = usParams.includes(embedParams)
          ? `${process.env.REACT_APP_USLOCATION}#/county`
          : worldParams.includes(embedParams)
            ? `${process.env.REACT_APP_WORLDLOCATION}#/country`
            : null;
        if (url) {
          window.open(`${url}/${clickedRegion.id}`);
          return null;
        }
      }

      if (fromWhichMap === 'usa' || fromWhichMap === 'world') {
        setForceMetricKey({ county: metricKey, state: metricKey });
      } else if (fromWhichMap === 'state' || fromWhichMap === 'country') {
        setForceMetricKey({ county: metricKey })
      }
      const population = populations[clickedRegion.id]
      if (population && regionsById[clickedRegion.id]) {
        const regionValue = isWorld
          ? getText(language, population.name)
          : `${population.CTYNAME}, ${population.stateAbbr}`;
        const subnational = regionsById[clickedRegion.id].subnational || false;
        updateSelectedRegionInUrl(
          clickedRegion.id,
          regionValue,
          {
            mode: 'map',
            map: fromWhichMap,
            subnational,
          },
        );
      }
    }
  }

  const closeRegion = (parentId) => {
    if (parentId) {
      clickRegion(routePath)({ id: parentId })({ defaultPrevented: false });
    } else {
      setRegionTextValue('')
      history.push('/')
    }
  }

  let selectedRegionData = null;
  let selectedSubnationalData = null;
  let selectedStateData = null
  const contentWidth = windowSize[0]// - 32 // 32 === margin
  const twoColumnLayout = (embedParams === null || embedParams === 'stateCounty') && contentWidth > 800 && !isWorld;
  // const twoColumnLayout = (embedParams === null || embedParams === 'stateCounty') && contentWidth > 800 && (!isWorld || selectedParentId);
  const detailedComponentWidths = twoColumnLayout ? contentWidth / 2 : contentWidth
  const subnationalTransition = transitioningOutId ? (
      (regionsById[transitioningOutId.previous].containsSubnational && regionsById[transitioningOutId.next].subnational)
        ||
      (regionsById[transitioningOutId.previous].subnational && regionsById[transitioningOutId.next].containsSubnational)
    ) : false;

  if (selectedRegionId) {
    if (!embedParams || (embedParams && (embedParams === 'stateCounty' || embedParams === 'countryRegion' || embedParams === routePath))) {
      if (selectedParentId) {
        // subnational
        selectedSubnationalData = (
          <div className={classnames({ transitionCountryMapDown: selectedParentId !== null })}>
            <RegionBlock
              {...props}
              width={detailedComponentWidths}
              regionType={routePath}
              selectedDatum={selectedRegion}
              selectedRegionId={selectedRegionId}
              clickRegion={clickRegion('subnational')}
              twoColumnLayout={twoColumnLayout}
              dateUpdated={dateUpdated}
              transitioningOut={transitioningOutRegion && !subnationalTransition}
              embedParams={embedParams}
              closeRegion={() => closeRegion(selectedParentId)}
              forceMetricKey={forceMetricKey.county}
              clearForceMetricKey={clearForceMetricKey}
              language={language}
              directionality={directionality}
              isWorld={isWorld}
              showUpdateMessage={showUpdateMessage}
              setShowUpdateMessage={setShowUpdateMessage}
            />
          </div>
        )
        // region
        selectedRegionData = embedParams === 'country' ? null : (
          <RegionBlock
            {...props}
            width={detailedComponentWidths}
            regionType={routePath}
            selectedDatum={selectedParentDatum}
            selectedRegionId={selectedParentId}
            clickRegion={clickRegion(routePath)}
            twoColumnLayout={twoColumnLayout}
            dateUpdated={dateUpdated}
            transitioningOut={transitioningOutRegion && !subnationalTransition}
            embedParams={embedParams}
            closeRegion={() => closeRegion(null)}
            forceMetricKey={forceMetricKey.county}
            clearForceMetricKey={clearForceMetricKey}
            language={language}
            directionality={directionality}
            isWorld={isWorld}
            showUpdateMessage={showUpdateMessage}
            setShowUpdateMessage={setShowUpdateMessage}
          />
        )
      } else {
        // region only
        selectedRegionData = (
          <RegionBlock
            {...props}
            width={detailedComponentWidths}
            regionType={routePath}
            selectedDatum={selectedRegion}
            selectedRegionId={selectedRegionId}
            clickRegion={clickRegion(routePath)}
            twoColumnLayout={twoColumnLayout}
            dateUpdated={dateUpdated}
            transitioningOut={transitioningOutRegion && !subnationalTransition}
            embedParams={embedParams}
            closeRegion={() => closeRegion(selectedParentId)}
            forceMetricKey={forceMetricKey.county}
            clearForceMetricKey={clearForceMetricKey}
            language={language}
            directionality={directionality}
            isWorld={isWorld}
            showUpdateMessage={showUpdateMessage}
            setShowUpdateMessage={setShowUpdateMessage}
          />
        )
      }
    }
  }

  if (selectedStateAbbr) {
    if (!embedParams || (embedParams && (embedParams === 'stateCounty'|| embedParams === 'state'))) {
      selectedStateData = (
        <RegionBlock
          {...props}
          width={detailedComponentWidths}
          regionType='state'
          selectedDatum={selectedRegion}
          selectedRegionId={selectedRegionId}
          clickRegion={clickRegion('state')}
          selectedStateAbbr={selectedStateAbbr}
          twoColumnLayout={twoColumnLayout}
          dateUpdated={dateUpdated}
          transitioningOut={transitioningOutState}
          embedParams={embedParams}
          closeRegion={() => closeRegion(null)}
          forceMetricKey={forceMetricKey.state}
          clearForceMetricKey={clearForceMetricKey}
          language={language}
          directionality={directionality}
          isWorld={isWorld}
          showUpdateMessage={showUpdateMessage}
          setShowUpdateMessage={setShowUpdateMessage}
        />
      )
    }
  }

  let topLevelMap = null;
  if (!isGoingToUS && (!embedParams || (embedParams && embedParams === type))) {
    topLevelMap = (
      <div className={classnames('topLevelMap', { transitionTopLevelMapDown, twoColumnLayout })}>
        <RegionBlock
          {...props}
          width={contentWidth}
          regionType={type}
          clickRegion={clickRegion(type)}
          dateUpdated={dateUpdated}
          embedParams={embedParams}
          language={language}
          directionality={directionality}
          isWorld={isWorld}
          showUpdateMessage={showUpdateMessage}
          setShowUpdateMessage={setShowUpdateMessage}
          goToWorldFromUS={goToWorldFromUS}
        />
      </div>
    )
  }

  return (
    <React.Fragment>
      {embedParams === null ?
        (
          <Header
            setRegionValue={setRegionValue}
            regionValue={regionTextValue}
            dateUpdated={dateUpdated}
            {...props}
            closeRegion={() => closeRegion(null)}
            className={classnames({ mobile: !twoColumnLayout, isWorld })}
            language={language}
            setLanguage={setLanguage}
            isWorld={isWorld}
          />
        )
      : null }
      <Switch>
        <Route path="/about">
          <About language={language} isWorld={isWorld}/>
        </Route>
        <Route path="/resources">
          <Resources language={language} />
        </Route>
        <Route path="/timelapse/:selectedStateAbbr">
          <Timelapse
            windowWidth={contentWidth}
            {...props}
          />
        </Route>
        <Route path="/timelapse">
          <Timelapse
            windowWidth={contentWidth}
            {...props}
          />
        </Route>

        <Route path="/">
          <div className={classnames('detailedRegions', { twoColumnLayout, transitionTopLevelMapDown, world: isWorld })}>
            {selectedSubnationalData}
            {selectedRegionData}
            {selectedStateData}
          </div>
          {topLevelMap}
        </Route>
      </Switch>
      <Footer
        dateUpdated={dateUpdated}
        embedParams={embedParams}
        language={language}
        directionality={directionality}
        isWorld={isWorld}
      />
    </React.Fragment>
  )
}

Viz.defaultProps = {
  language: 'en',
  directionality: 'ltr',
}
